import { Action, createReducer, on } from '@ngrx/store';
import { omit } from 'lodash-es';

import { MarketComparisonActions } from './market-comparison.actions';
import { MarketComparisonState } from './market-comparison.state';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';

export const initialState: MarketComparisonState = {
  loading: false,
  termMeta: {},
  termAdditionalInfo: {},
  preview: false,
  updatingDatapoint: false,
  filtersByColumnIndex: {},

  diffLoading: false,
  diffList: {},
  diffColumns: {},
  diffBaseline: {},
  diffMaximums: {},
  diffCompleted: false,
  diffLoadingByColumns: {},

  groundTruthDiffs: {},
  groundTruthDiffColumns: {},
  groundTruthDiffMaximums: {},
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      MarketComparisonActions.Reset,
      (state): MarketComparisonState => ({
        ...initialState,
        loading: false,
        appliedFilter: state.appliedFilter,
        termMeta: state.termMeta,
      })
    ),

    on(
      MarketComparisonActions.LoadTermListSuccess,
      (state, action): MarketComparisonState => ({
        ...state,
        partialTerm: action.partialTerm,
      })
    ),
    on(
      MarketComparisonActions.LoadTermListFail,
      (state, action): MarketComparisonState => ({ ...state, error: action.error, loading: false })
    ),

    on(
      MarketComparisonActions.LoadTermMetaSuccess,
      (state, action): MarketComparisonState => ({
        ...state,
        termMeta: { ...state.termMeta, [action.meta.meta.term]: action.meta.meta },
        termAdditionalInfo: { ...state.termAdditionalInfo, ...action.meta.statistics },
      })
    ),

    on(
      MarketComparisonActions.LoadStatisticsSuccess,
      (state, action): MarketComparisonState => ({
        ...state,
        termAdditionalInfo: { ...state.termAdditionalInfo, ...action.statistics },
      })
    ),
    on(
      MarketComparisonActions.LoadTermMetaFail,
      (state, action): MarketComparisonState => ({ ...state, error: action.error })
    ),
    on(
      MarketComparisonActions.SetPreview,
      (state, action): MarketComparisonState => ({
        ...state,
        preview: action.preview,
      })
    ),
    on(MarketComparisonActions.SortList, (state, action): MarketComparisonState => ({ ...state, sort: action.sort })),
    on(MarketComparisonActions.ClearDiff, (state): MarketComparisonState => ({ ...state, diffColumns: {} })),
    on(MarketComparisonActions.MakeDiff, (state, action): MarketComparisonState => ({ ...state, diffLoading: true })),
    on(
      MarketComparisonActions.MakeDiffSuccess,
      (state, action): MarketComparisonState => ({
        ...state,
        diffBaseline: { ...state.diffBaseline, [action.columnId]: action.baseline },
        diffList: { ...state.diffList, [action.columnId]: action.diffValues },
        diffMaximums: { ...state.diffMaximums, [action.columnId]: action.diffMaximums },
      })
    ),
    on(
      MarketComparisonActions.SetDiff,
      (state, action): MarketComparisonState => ({
        ...state,
        diffLoading: false,
        diffColumns: { ...state.diffColumns, [action.columnId]: action.value },
      })
    ),
    on(
      MarketComparisonActions.MakeDiffFail,
      (state, action): MarketComparisonState => ({
        ...state,
        diffLoading: false,
        error: action.error,
      })
    ),

    on(
      MarketComparisonActions.UpdateDatapoint,
      MarketComparisonActions.UpdateDatapoints,
      MarketComparisonActions.ClearAllGroundTruth,
      (state, action): MarketComparisonState => ({ ...state, updatingDatapoint: true })
    ),
    on(
      MarketComparisonActions.SetTransaction,
      (state, action): MarketComparisonState => ({ ...state, transactionId: action.transactionId })
    ),
    on(
      MarketComparisonActions.SetOpenedColumn,
      (state, action): MarketComparisonState => ({
        ...state,
        openedColumnIndex: action.openedColumnIndex,
      })
    ),
    on(
      MarketComparisonActions.UpdateDatapointFail,
      MarketComparisonActions.UpdateDatapointSuccess,
      MarketComparisonActions.UpdateDatapointsFail,
      MarketComparisonActions.UpdateDatapointsSuccess,
      MarketComparisonActions.ClearAllGroundTruthSuccess,
      MarketComparisonActions.ClearAllGroundTruthFail,
      (state, action): MarketComparisonState => ({ ...state, updatingDatapoint: false })
    ),
    on(
      MarketComparisonActions.UpdateDatapointSuccess,
      (state, action): MarketComparisonState => ({
        ...state,
        selectedDatapointForEdit:
          state.selectedDatapointForEdit?.datapointField?.fieldHash === action.event.datapointHash
            ? {
                ...state.selectedDatapointForEdit,
                datapointValue: action.event.value,
                sourceReferences: action.event.sourceReferences,
              }
            : state.selectedDatapointForEdit,
      })
    ),
    on(
      MarketComparisonActions.UpdateSelectedDatapointForEdit,
      (state, action): MarketComparisonState => ({
        ...state,
        selectedDatapointForEdit: action,
      })
    ),
    on(
      MarketComparisonActions.AddFilter,
      (state, action): MarketComparisonState => ({
        ...state,
        filtersByColumnIndex: {
          ...state.filtersByColumnIndex,
          [action.columnIndex]: action.filter,
        },
      })
    ),
    on(
      MarketComparisonActions.ClearFilter,
      (state, action): MarketComparisonState => ({
        ...state,
        filtersByColumnIndex: {},
      })
    ),
    on(
      MarketComparisonActions.MakeGroundTruthDiffSuccess,
      (state, action): MarketComparisonState => ({
        ...state,
        groundTruthDiffs: action.diffValues
          ? { ...state.groundTruthDiffs, [action.columnId]: action.diffValues }
          : omit(state.groundTruthDiffs, action.columnId),
        groundTruthDiffColumns: action.diffValues
          ? { ...state.groundTruthDiffColumns, [action.columnId]: true }
          : omit(state.groundTruthDiffColumns, action.columnId),
        groundTruthDiffMaximums: action.diffMaximums
          ? { ...state.groundTruthDiffMaximums, [action.columnId]: action.diffMaximums }
          : omit(state.groundTruthDiffMaximums, action.columnId),
      })
    ),

    on(
      MarketComparisonActions.SetFirstLevelPanel,
      (state, action): MarketComparisonState => ({ ...state, firstLevelPanel: action.panel })
    ),
    on(
      MarketComparisonActions.SetSecondLevelPanel,
      (state, action): MarketComparisonState => ({ ...state, secondLevelPanel: action.panel })
    ),

    on(
      MarketComparisonActions.SetRenderedRange,
      (state, action): MarketComparisonState => ({
        ...state,
        renderedRange: { start: action.start, end: action.end },
      })
    )
  )
);

export function reducer(state: MarketComparisonState | undefined, action: Action) {
  return featureReducer(state, action);
}
